import { useEffect, useMemo, useRef, useState } from 'react'
import { useLessonRun } from '../preview/useLessonRun'
import { buildPreviewRun } from '../preview/previewRun'
// src/features/editor/components/studio/MobilePreviewFrame.tsx
import { EmptyCanvasMascot } from './EmptyCanvasMascot'
import { LessonLookPreview } from './LessonLookPreview'
import { VideoViewportSwitch } from './VideoViewportSwitch'
import {
  PreviewViewportContext,
  VIEWPORTS,
  type PreviewViewport,
} from '@/features/editor/components/blocks/previews/previewViewports'
import { cn } from '@/lib/utils'
import { useVideoOrientation } from '@/features/editor/components/shared/useVideoOrientation'
import { LiveQuizMascot } from '@/features/editor/components/blocks/previews/quizMascotContext'
import { CanvasVideoDrop } from '@/features/editor/components/blocks/previews/canvasVideoDrop'
import type { CourseWithoutBlocks } from '@/types/course'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectBlock } from '@/features/editor/editorSlice'
import { playNextSound } from '@/lib/sound'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useGetLessonBlocksQuery } from '@/features/editor/lessonsApi'
import { renderBlockPreview } from '@/features/editor/components/blocks/previews/renderBlockPreview'
import { StrictLangContext } from '@/features/editor/components/blocks/previews/design/strictLangContext'
import { PhoneCanvas } from '@/features/editor/components/blocks/previews/PhoneCanvas'
import { renderKeyOf } from '@/features/editor/lib/blockRenderKeys'
import { LessonProgressContext } from '@/features/editor/components/blocks/previews/lessonProgress'
import { useIsCourseAuthor } from '@/features/editor/hooks/useIsCourseAuthor'
import { isSameBlock } from './blockDraftSync'
import { useBlockDraftContext } from './BlockDraftContext'
import { isBlockLocked, useBlocksInRun } from './useBlocksInRun'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
}

/** Блоки, которые на холсте можно пройти (есть что ответить). */
const INTERACTIVE_TYPES = new Set(['quiz', 'match'])

export const MobilePreviewFrame = ({ course }: Props) => {
  const m = useMessages(messages)
  const selectedLessonId = useAppSelector((s) => s.editor.selectedLessonId)
  const selectedBlockId = useAppSelector((s) => s.editor.selectedBlockId)
  const contentLang = useAppSelector((s) => s.editor.contentLang)
  const lessonLookPreview = useAppSelector((s) => s.editor.lessonLookPreview)
  const dispatch = useAppDispatch()
  const isCourseAuthor = useIsCourseAuthor(course)
  const blocksInRun = useBlocksInRun(course.id)

  const { data: blocks = [] } = useGetLessonBlocksQuery(selectedLessonId ?? skipToken)
  const { draft, setDraft } = useBlockDraftContext()

  const idAliases = useAppSelector((s) => s.editor.idAliases)
  // Через алиасы: после первой правки живого курса выделение уже на id
  // черновика, а список урока ещё со старыми (см. `isSameBlock`).
  const blockIndex = blocks.findIndex((b) => isSameBlock(idAliases, b.id, selectedBlockId))
  const serverBlock = blockIndex >= 0 ? blocks[blockIndex] : null
  // Живое превью: пока блок правится, рисуем драфт (тот же блок), иначе серверные данные.
  const block =
    draft && serverBlock && isSameBlock(idAliases, serverBlock.id, draft.id) ? draft : serverBlock
  // Ref: progress reflects the real block position in the lesson (current / total).
  // «Далее» на холсте выбирает следующий экран урока — автор проходит урок прямо
  // здесь, не открывая предпросмотр. На последнем экране кнопке некуда вести.
  const nextBlock = blockIndex >= 0 ? blocks[blockIndex + 1] : undefined
  // «Назад» слева от CTA — как в плеере: выбирает предыдущий экран; на первом
  // кнопки нет.
  const prevBlock = blockIndex > 0 ? blocks[blockIndex - 1] : undefined
  const progress = {
    total: Math.max(blocks.length, 1),
    current: Math.max(blockIndex, 0),
    onNext: nextBlock
      ? () => {
          playNextSound()
          dispatch(selectBlock(nextBlock.id))
        }
      : undefined,
    onBack: prevBlock ? () => dispatch(selectBlock(prevBlock.id)) : undefined,
  }
  // Квиз и сопоставление на холсте проходятся, как в приложении: выбрал
  // ответ → «Подтвердить» → «Правильно/Неправильно» → «Далее» ведёт на следующий
  // блок урока (на последнем — пройти заново). Та же механика, что в предпросмотре
  // урока (`useLessonRun`), на одном блоке. Пустой квиз остаётся статичным — с
  // серыми подсказками автору.
  const runBlocks = useMemo(() => (block ? [block] : []), [block])
  const lessonRun = useLessonRun(runBlocks, contentLang, block?.id ?? null)
  const isInteractive = block !== null && INTERACTIVE_TYPES.has(block.type) && lessonRun.isGradable
  const canvasRun = isInteractive
    ? {
        ...buildPreviewRun(lessonRun, contentLang),
        onCanvas: true,
        onCta: lessonRun.verdict === null ? lessonRun.check : (progress.onNext ?? lessonRun.reset),
      }
    : undefined
  // Запертый блок (прогон, проверка курса) на канвасе не правится: правка
  // пачкала бы черновик, чьё «Сохранить» погашено тем же локом.
  const canEditCanvas = isCourseAuthor && block !== null && !isBlockLocked(blocksInRun, block.id)

  // Горизонтальный ролик — экран шире: плеер и полоска растягиваются по видео,
  // кнопка остаётся ширины телефона (`PreviewShell`).
  const videoId =
    block?.type === 'video' ? (contentLang === 'ru' ? block.videoId : block.videoIdEn) : null
  const isLandscapeVideo = useVideoOrientation(videoId) === 'landscape'
  // Экран просмотра — только для горизонтального ролика; смена блока или ролика
  // возвращает к телефону (ключ — выбранный блок и ролик).
  const viewKey = `${block?.id ?? ''}|${videoId ?? ''}`
  const [view, setView] = useState<{ key: string; viewport: PreviewViewport }>({
    key: viewKey,
    viewport: 'phone',
  })
  const viewport: PreviewViewport =
    isLandscapeVideo && view.key === viewKey ? view.viewport : 'phone'
  const { width: vw, height: vh } = VIEWPORTS[viewport]
  // Анимация только на повороте телефона (телефон ⇄ телефон боком), не на ПК
  // и не при первом показе.
  const prevViewport = useRef(viewport)
  const rotateAnim =
    prevViewport.current !== viewport &&
    viewport !== 'desktop' &&
    prevViewport.current !== 'desktop'
      ? viewport === 'phoneLandscape'
        ? 'animate-[phone-rotate-landscape_550ms_cubic-bezier(0.2,0.8,0.2,1)]'
        : 'animate-[phone-rotate-portrait_550ms_cubic-bezier(0.2,0.8,0.2,1)]'
      : null
  useEffect(() => {
    prevViewport.current = viewport
  }, [viewport])

  const hasLessons = course.lessons.length > 0
  const emptyMessage = hasLessons ? m.selectBlockForPreview : m.addLessonFirst

  return (
    <section
      data-testid="mobile-preview"
      className={cn(
        'flex h-full w-full items-center justify-center overflow-hidden p-4 transition-[padding] duration-300',
        // Широкий экран (повёрнутый телефон, ПК) не заходит под переключатель вида
        // и инструменты справа внизу — снизу оставляем им место.
        viewport === 'phone' ? null : 'pb-20',
      )}
    >
      {/* Блок не выбран — экрана телефона нет вовсе: маскот стоит прямо на холсте. */}
      {block ? (
        <div
          // Ключ — общий с лентой (`renderKeyOf`): замена временной записи
          // настоящим блоком не пересоздаёт телефон. Перетекание из примера в
          // новый блок делает окно выбора типа (`AddBlockPicker`).
          // Черновик живого курса приходит с новым id, но тем же `originId`: без него
          // первая правка пересоздавала телефон, и поле теряло фокус и клавиатуру.
          key={block.originId ?? renderKeyOf(block.id)}
          className="relative h-full max-w-full"
          style={{ aspectRatio: `${vw} / ${vh}` }}
        >
          {/* Поворот телефона — с анимацией разворота, как у айфона: новый экран
              появляется повёрнутым на 90° и встаёт на место. Ключ перезапускает её. */}
          <div key={viewport} className={cn('size-full', rotateAnim)}>
            <LiveQuizMascot>
              <CanvasVideoDrop enabled={canEditCanvas}>
                <PhoneCanvas className="rounded-2xl shadow-sm" viewport={viewport}>
                  <PreviewViewportContext.Provider value={viewport}>
                    <LessonProgressContext.Provider value={progress}>
                      {/* Холст — строго язык правки, как и миниатюры ленты. */}
                      <StrictLangContext.Provider value>
                        {renderBlockPreview(block, contentLang, {
                          run: canvasRun,
                          onDesignChange: canEditCanvas
                            ? (elements) =>
                                setDraft((prev) =>
                                  prev && prev.type === 'design' ? { ...prev, elements } : prev,
                                )
                            : undefined,
                          // Пустой блок статьи: выбор пишется в черновик, `PUT` шлёт
                          // автосохранение панели. Временная запись (id < 0, сервер
                          // ещё создаёт блок) выбор не принимает — он бы пропал.
                          onArticlePick:
                            canEditCanvas && block.id > 0
                              ? (articleId, article) =>
                                  setDraft((prev) =>
                                    prev && prev.type === 'article' && prev.id === block.id
                                      ? { ...prev, articleId, article, available: true }
                                      : prev,
                                  )
                              : undefined,
                        })}
                      </StrictLangContext.Provider>
                    </LessonProgressContext.Provider>
                  </PreviewViewportContext.Provider>
                </PhoneCanvas>
              </CanvasVideoDrop>
            </LiveQuizMascot>
          </div>
        </div>
      ) : lessonLookPreview ? (
        <>
          <LessonLookPreview
            theme={lessonLookPreview.theme}
            pattern={lessonLookPreview.pattern}
            contentLang={contentLang}
          />
          <div className="lg:hidden">
            <EmptyCanvasMascot message={emptyMessage} />
          </div>
        </>
      ) : (
        <EmptyCanvasMascot message={emptyMessage} />
      )}
      {isLandscapeVideo ? (
        // Слева внизу, на одном уровне с инструментами (языки, отмена, звук).
        <div className="absolute bottom-3 left-3 z-20">
          <VideoViewportSwitch
            value={viewport}
            onChange={(next) => setView({ key: viewKey, viewport: next })}
          />
        </div>
      ) : null}
    </section>
  )
}
